import React from 'react';
import { WhatsAppNotificationConfig } from '../../types/whatsapp';

interface ConfigurationListProps {
  configurations: WhatsAppNotificationConfig[];
  onEdit: (config: WhatsAppNotificationConfig) => void;
  onDelete: (configId: number) => void;
  onActivate: (configId: number) => void;
}

const ConfigurationList: React.FC<ConfigurationListProps> = ({
  configurations,
  onEdit,
  onDelete,
  onActivate
}) => {
  const formatDays = (days: string) => {
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const dayNumbers = days.split(',').map(d => parseInt(d.trim()));
    return dayNumbers.map(num => dayNames[num]).join(', ');
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (configurations.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay configuraciones</h3>
          <p className="mt-1 text-sm text-gray-500">
            Comienza creando tu primera configuración de notificaciones WhatsApp.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Configuraciones de Notificaciones
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Lista de todas las configuraciones de notificaciones WhatsApp
        </p>
      </div>
      
      <div className="border-t border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Configuración
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Horario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Días
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contactos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Última modificación
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {configurations.map((config) => (
                <tr key={config.id} className={config.esActiva ? 'bg-green-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center">
                          {config.nombre}
                          {config.esActiva && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Activa
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {config.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 h-2.5 w-2.5 rounded-full mr-2 ${
                        config.notificacionesHabilitadas ? 'bg-green-400' : 'bg-red-400'
                      }`}></div>
                      <span className="text-sm text-gray-900">
                        {config.notificacionesHabilitadas ? 'Habilitadas' : 'Deshabilitadas'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {config.horaInicio} - {config.horaFin}
                    <div className="text-xs text-gray-500">
                      Monitor: {config.intervaloMonitoreoMinutos}m | Reportes: {config.intervaloReporteMinutos}m
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDays(config.diasNotificacion)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <span className="mr-2">{config.contactos.length}</span>
                      <div className="text-xs text-gray-500">
                        ({config.contactos.filter(c => c.esActivo).length} activos)
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(config.fechaModificacion)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {!config.esActiva && (
                        <button
                          onClick={() => onActivate(config.id)}
                          className="text-green-600 hover:text-green-900 text-sm"
                          title="Activar configuración"
                        >
                          Activar
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(config)}
                        className="text-indigo-600 hover:text-indigo-900 text-sm"
                        title="Editar configuración"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => onDelete(config.id)}
                        className="text-red-600 hover:text-red-900 text-sm"
                        title="Eliminar configuración"
                        disabled={config.esActiva}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationList;
